import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {ItemRefinementObject, ItemRefinementSource, ItemRefinementStrokeObject} from '@PosterWhiteboard/classes/item-refinement.types';
import {compositeRefinedCutout, getRefinementMaskSize, hasRestoreStrokes, rasterizeRefinement, REFINEMENT_GRID_SIZE} from '@Libraries/background-refinement.library';
import {loadImageAsync, loadImageWithRetriesAsync} from '@Utils/image.util';
import type {DeepPartial} from '@/global';

/**
 * The Eraser's strokes on an image item, a sibling of crop and masking rather than part of background
 * removal: the brushes work on any image, removed background or not. An unrefined image serializes as an
 * explicit null (never undefined, so undoing the first Apply round-trips as a modify in poster history),
 * and no strokes means no refinement, the same rule the refine controller and the PHP VO apply.
 */
export class ItemRefinement extends CommonMethods {
  public strokes: ItemRefinementStrokeObject[] = [];

  private readonly item: ItemRefinementSource;

  constructor(item: ItemRefinementSource) {
    super();
    this.item = item;
  }

  public override copyVals(obj: DeepPartial<ItemRefinementObject> | null = {}): void {
    // Explicit null: clear the strokes (how a removal survives the jsondiffpatch history round-trip).
    if (obj === null) {
      this.strokes = [];
      return;
    }
    // Key absent: not part of this update — leave the current strokes as-is.
    if (obj.strokes !== undefined) {
      this.strokes = obj.strokes;
    }
  }

  public toObject(): ItemRefinementObject | null {
    return this.isRefined() ? {strokes: this.strokes} : null;
  }

  public isRefined(): boolean {
    return this.strokes.length > 0;
  }

  /**
   * A URL with the stored refinement painted in, for surfaces that take image URLs rather than elements
   * (the masking modal).
   */
  public async getRefinedImageUrl(imageUrl: string): Promise<string> {
    if (!this.isRefined()) {
      return imageUrl;
    }
    return (await this.applyRefinementToImage(await loadImageWithRetriesAsync(imageUrl))).src;
  }

  /**
   * Applies the user's erase/restore refinement on top of the loaded image: the cutout when the background
   * is removed, the plain image otherwise. Restore paints from the original, which is only fetched when a
   * restore stroke exists. The masks are rasterized no larger than the stroke grid, whatever size the image
   * loads at: stored points carry no more detail, and a high-res render would otherwise allocate them at the
   * full upload size. Falls back to the unrefined image if compositing fails: that beats an item (or a whole
   * server render) that fails to load.
   */
  public async applyRefinementToImage(baseImage: HTMLImageElement): Promise<HTMLImageElement> {
    if (!this.isRefined()) {
      return baseImage;
    }

    try {
      const original = hasRestoreStrokes(this.strokes) ? await this.#loadOriginalImage() : null;
      const maskSize = getRefinementMaskSize(baseImage.width, baseImage.height, REFINEMENT_GRID_SIZE);
      const masks = rasterizeRefinement(this.toObject(), maskSize.width, maskSize.height);
      const refined = compositeRefinedCutout({
        cutout: baseImage,
        original,
        ...masks,
        width: baseImage.width,
        height: baseImage.height,
      });
      return await loadImageAsync(refined.toDataURL('image/png'));
    } catch (error) {
      console.error('Refinement could not be applied; showing the unrefined image', error);
      return baseImage;
    }
  }

  /**
   * The pre-removal image. Null when it cannot be loaded: the erase strokes still apply and only the restores
   * are skipped, rather than losing the whole refinement.
   */
  async #loadOriginalImage(): Promise<HTMLImageElement | null> {
    try {
      return await loadImageWithRetriesAsync(this.item.getImageUrlWithBackground());
    } catch (error) {
      console.error('Original image could not be loaded; restore strokes are skipped', error);
      return null;
    }
  }
}
